import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { presenceAPI } from "../services/api";
import { PageWrapper, Card, Button, Logo, SharePresenceCard, useCompletionGlow, FullScreenGlow } from "../components";
import { Flame, Check, ChevronLeft, ChevronRight, Calendar, Sparkles, Info, Share2 } from "lucide-react";

export const DailyPresencePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reflection, setReflection] = useState("");
  const [completing, setCompleting] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showStats, setShowStats] = useState(() => searchParams.get("view") === "history");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCompletionGlow, setShowCompletionGlow] = useState(false);
  const [stats, setStats] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchPresence();
  }, []);

  const fetchPresence = async () => {
    try {
      const [todayRes, statsRes] = await Promise.all([
        presenceAPI.getToday(),
        presenceAPI.getStats()
      ]);
      setData(todayRes.data);
      setStats(statsRes.data);
      if (todayRes.data.entry?.reflection) {
        setReflection(todayRes.data.entry.reflection);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendar = async (month) => {
    try {
      const res = await presenceAPI.getHistory(month);
      setCalendarData(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (showStats) {
      fetchCalendar(currentMonth);
    }
  }, [showStats, currentMonth]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await presenceAPI.complete(reflection, data.prompt.text);
      
      // Trigger completion glow
      setShowCompletionGlow(true);
      setTimeout(() => setShowCompletionGlow(false), 1500);
      
      setShowCompletion(true);
      
      // Refresh data after a moment
      setTimeout(async () => {
        await fetchPresence();
        setShowCompletion(false);
      }, 4000);
    } catch (e) {
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  const changeMonth = (direction) => {
    const [year, month] = currentMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCurrentMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  if (loading) {
    return (
      <PageWrapper showNav={false}>
        <div className="flex items-center justify-center h-screen">
          <div className="w-12 h-12 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  // Completion Animation Overlay
  if (showCompletion) {
    return (
      <PageWrapper showNav={false}>
        {/* Completion Glow Effect */}
        <FullScreenGlow show={showCompletionGlow} />
        
        <div className="min-h-screen bg-[#FAFAF7] flex flex-col items-center justify-center px-6 animate-fade-in">
          {/* Glow Circle with golden completion glow */}
          <div className="relative mb-10">
            <div className="absolute inset-0 w-32 h-32 bg-[#B8956C]/30 rounded-full animate-ping" />
            <div className="absolute inset-0 w-32 h-32 bg-[#B8956C]/15 rounded-full animate-pulse" />
            <div className="relative w-32 h-32 bg-gradient-to-br from-[#4A6741] to-[#5A7D4F] rounded-full flex items-center justify-center shadow-2xl completion-glow">
              <Check size={48} className="text-white animate-fade-in-up check-glow" />
            </div>
          </div>

          {/* Message */}
          <div className="text-center space-y-6 max-w-sm animate-fade-in-up stagger-1">
            <h2 className="font-heading text-3xl text-[#1F2937]">Presence Complete</h2>
            
            <div className="py-8 space-y-4">
              <p className="text-[#6B7280] text-lg italic leading-relaxed">
                Take a breath.
              </p>
              <p className="text-[#6B7280] text-lg italic leading-relaxed">
                Close your eyes for a moment.
              </p>
              <p className="text-[#6B7280] text-lg italic leading-relaxed">
                Feel the shift.
              </p>
            </div>

            {stats && (
              <div className="flex items-center justify-center gap-2 text-[#D4A373] mb-6">
                <Flame size={20} />
                <span className="font-medium">{stats.current_streak + 1} day streak</span>
              </div>
            )}

            {/* Share Button */}
            <button
              onClick={() => {
                setShowCompletion(false);
                setShowShareModal(true);
                fetchPresence(); // Refresh data
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full border border-[#E5E7EB] text-[#4A6741] font-medium shadow-sm hover:shadow-md hover:border-[#4A6741] transition-all animate-fade-in-up stagger-2"
              data-testid="share-presence-btn"
            >
              <Share2 size={18} />
              Share a Moment of Presence
            </button>
          </div>
        </div>

        {/* Share Modal */}
        <SharePresenceCard
          userReflection={reflection}
          isVisible={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      </PageWrapper>
    );
  }

  // Stats/Calendar View
  if (showStats) {
    return (
      <PageWrapper showNav={false}>
        <div className="min-h-screen bg-[#FAFAF7]">
          {/* Header */}
          <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB]/50 z-40 safe-area-pt">
            <div className="max-w-lg mx-auto px-6 h-14 flex items-center justify-between">
              <button
                onClick={() => setShowStats(false)}
                className="p-2 -ml-2 hover:bg-[#F3F4F6] rounded-xl transition-colors active:scale-95"
                data-testid="back-from-history"
              >
                <ChevronLeft size={24} className="text-[#1F2937]" />
              </button>
              <h1 className="font-heading text-lg text-[#1F2937]">Presence Journey</h1>
              <div className="w-10" />
            </div>
          </header>

          <div className="px-6 py-8 space-y-8 max-w-lg mx-auto pb-20">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3 animate-fade-in-up">
              <Card className="p-4 text-center">
                <Flame className="mx-auto mb-2 text-[#D4A373]" size={22} />
                <p className="font-heading text-2xl text-[#1F2937]">{stats?.current_streak || 0}</p>
                <p className="text-[10px] sm:text-xs text-[#6B7280]">Current Streak</p>
              </Card>
              <Card className="p-4 text-center">
                <Sparkles className="mx-auto mb-2 text-[#4A6741]" size={22} />
                <p className="font-heading text-2xl text-[#1F2937]">{stats?.total_days || 0}</p>
                <p className="text-[10px] sm:text-xs text-[#6B7280]">Total Days</p>
              </Card>
              <Card className="p-4 text-center">
                <Calendar className="mx-auto mb-2 text-[#6B7280]" size={22} />
                <p className="font-heading text-2xl text-[#1F2937]">{stats?.this_month || 0}</p>
                <p className="text-[10px] sm:text-xs text-[#6B7280]">This Month</p>
              </Card>
            </div>

            {/* Longest Streak */}
            {stats?.longest_streak > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-[#6B7280] animate-fade-in-up">
                <span>Longest streak:</span>
                <span className="font-medium text-[#D4A373]">{stats.longest_streak} days</span>
              </div>
            )}

            {/* Calendar */}
            <Card className="p-5 sm:p-6 animate-fade-in-up stagger-1">
              <div className="flex items-center justify-between mb-5">
                <button
                  onClick={() => changeMonth(-1)}
                  className="p-2 hover:bg-[#F3F4F6] rounded-xl transition-colors active:scale-95"
                  data-testid="prev-month"
                >
                  <ChevronLeft size={20} className="text-[#6B7280]" />
                </button>
                <h3 className="font-heading text-base sm:text-lg text-[#1F2937]">
                  {new Date(currentMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => changeMonth(1)}
                  className="p-2 hover:bg-[#F3F4F6] rounded-xl transition-colors active:scale-95"
                  data-testid="next-month"
                >
                  <ChevronRight size={20} className="text-[#6B7280]" />
                </button>
              </div>

              {/* Calendar Grid */}
              <PresenceCalendar 
                month={currentMonth} 
                completedDates={calendarData?.completed_dates || []} 
              />
            </Card>

            {/* Streak Info */}
            <div className="flex items-start gap-3 p-4 bg-[#4A6741]/5 rounded-2xl animate-fade-in-up stagger-1">
              <Info size={18} className="text-[#4A6741] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#6B7280]">
                <p className="font-medium text-[#1F2937] mb-1">How streaks work</p>
                <p>Your streak counts consecutive days of completed presence. Missing a day resets your streak to zero. Complete today's presence to keep your streak alive!</p>
              </div>
            </div>

            {/* Recent Reflections */}
            {stats?.recent_entries?.filter(e => e.reflection).length > 0 && (
              <div className="space-y-4 animate-fade-in-up stagger-2">
                <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B7280] font-medium">
                  Recent Reflections
                </h3>
                <div className="space-y-3">
                  {stats.recent_entries.filter(e => e.reflection).slice(0, 5).map((entry) => (
                    <Card key={entry.id} className="p-4">
                      <p className="text-sm text-[#6B7280] mb-2">
                        {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      </p>
                      <p className="text-[#1F2937] italic leading-relaxed">"{entry.reflection}"</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </PageWrapper>
    );
  }

  // Main Presence View
  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen flex flex-col bg-[#FAFAF7]">
        {/* Header */}
        <header className="px-6 pt-8 pb-4 safe-area-pt">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-[#F3F4F6] rounded-xl transition-colors active:scale-95"
              data-testid="back-from-presence"
            >
              <ChevronLeft size={24} className="text-[#1F2937]" />
            </button>
            <Logo size="sm" variant="dark" />
            <button
              onClick={() => setShowStats(true)}
              className="p-2 -mr-2 hover:bg-[#F3F4F6] rounded-xl transition-colors active:scale-95"
              data-testid="show-stats-btn"
            >
              <Calendar size={22} className="text-[#6B7280]" />
            </button>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex flex-col px-6 pb-12 max-w-lg mx-auto w-full safe-area-pb">
          {/* Streak Badge */}
          {data?.streak > 0 && (
            <div className="flex items-center justify-center gap-2 mb-8 animate-fade-in-up">
              <Flame size={18} className="text-[#D4A373]" />
              <span className="text-sm font-medium text-[#D4A373]">{data.streak} day streak</span>
            </div>
          )}

          {/* Date */}
          <p className="text-center text-sm text-[#6B7280] uppercase tracking-[0.2em] mb-4 animate-fade-in-up">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>

          {/* Title */}
          <h1 className="font-heading text-2xl text-center text-[#1F2937] mb-12 animate-fade-in-up stagger-1">
            Daily Presence
          </h1>

          {/* Prompt */}
          <div className="flex-1 flex flex-col justify-center -mt-16">
            <p className="font-heading text-2xl sm:text-3xl text-center text-[#1F2937] leading-relaxed mb-12 animate-fade-in-up stagger-2">
              {data?.prompt?.text}
            </p>

            {/* Reflection Input */}
            {!data?.completed && (
              <div className="space-y-6 animate-fade-in-up stagger-3">
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="Write a reflection... (optional)"
                  className="w-full h-32 px-5 py-4 bg-white rounded-2xl border-2 border-[#E5E7EB] 
                    text-[#1F2937] placeholder:text-[#9CA3AF] resize-none text-base
                    focus:outline-none focus:border-[#4A6741] focus:shadow-[0_0_0_4px_rgba(74,103,65,0.08)]
                    transition-all duration-300"
                  data-testid="presence-reflection"
                />

                <Button
                  onClick={handleComplete}
                  disabled={completing}
                  className="w-full"
                  data-testid="complete-presence-btn"
                >
                  {completing ? "Completing..." : "Complete Today's Presence"}
                </Button>
              </div>
            )}

            {/* Already Completed State */}
            {data?.completed && (
              <div className="text-center space-y-6 animate-fade-in-up">
                <div className="w-20 h-20 bg-gradient-to-br from-[#4A6741] to-[#5A7D4F] rounded-full flex items-center justify-center mx-auto shadow-lg">
                  <Check size={32} className="text-white" />
                </div>
                <div className="space-y-2">
                  <p className="text-[#4A6741] font-medium">Today's presence complete</p>
                  <p className="text-sm text-[#6B7280]">Return tomorrow for your next reflection</p>
                  {data?.streak > 0 && (
                    <div className="flex items-center justify-center gap-2 mt-3 text-[#D4A373]">
                      <Flame size={16} />
                      <span className="text-sm font-medium">{data.streak} day streak</span>
                    </div>
                  )}
                </div>
                {data.entry?.reflection && (
                  <Card className="p-5 text-left mt-6">
                    <p className="text-xs text-[#6B7280] uppercase tracking-wide mb-2">Your reflection</p>
                    <p className="text-[#1F2937] italic leading-relaxed">"{data.entry.reflection}"</p>
                  </Card>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col items-center gap-3 mt-6">
                  {/* Share Button */}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full border border-[#E5E7EB] text-[#4A6741] font-medium shadow-sm hover:shadow-md hover:border-[#4A6741] transition-all"
                    data-testid="share-presence-completed-btn"
                  >
                    <Share2 size={16} />
                    Share a Moment of Presence
                  </button>

                  {/* View History Link */}
                  <button
                    onClick={() => setShowStats(true)}
                    className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#4A6741] transition-colors"
                    data-testid="view-full-history"
                  >
                    <Calendar size={16} />
                    <span>View your presence journey</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <SharePresenceCard
        userReflection={data?.entry?.reflection}
        isVisible={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </PageWrapper>
  );
};

// Calendar Component
const PresenceCalendar = ({ month, completedDates }) => {
  const [year, monthNum] = month.split('-').map(Number);
  const firstDay = new Date(year, monthNum - 1, 1);
  const lastDay = new Date(year, monthNum, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay(); // 0 = Sunday

  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const today = new Date().toISOString().split('T')[0];

  const completedSet = new Set(completedDates);

  return (
    <div>
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map((day, i) => (
          <div key={i} className="text-center text-xs text-[#9CA3AF] font-medium py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before the 1st */}
        {Array.from({ length: startingDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}

        {/* Days of the month */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dateStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          const isCompleted = completedSet.has(dateStr);
          const isToday = dateStr === today;

          return (
            <div
              key={dayNum}
              className={`
                aspect-square flex items-center justify-center rounded-full text-sm
                transition-all duration-200
                ${isCompleted 
                  ? 'bg-[#4A6741] text-white' 
                  : isToday 
                    ? 'bg-[#F3F4F6] text-[#1F2937] ring-2 ring-[#4A6741]'
                    : 'text-[#6B7280]'
                }
              `}
            >
              {dayNum}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyPresencePage;
